import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { API_CONFIG } from '../config/api';
import { assessmentUpdateNotifier } from '../utils/assessmentUpdateNotifier';

interface AssessmentResponse {
  questionId: string;
  answer: number;
  timestamp: Date;
}

interface SocioRelationalData {
  responses: AssessmentResponse[];
  scores: {
    overall: number;
    socioRelationalScore: number;
    subscales?: {
      significantOther: number;
      family: number;
      friends: number;
    };
  };
  results: {
    level: 'Low' | 'Moderate' | 'High';
    interpretation: string;
    recommendations: string[];
  };
}

const SocioRelationalAssessmentFormPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [currentQuestion, setCurrentQuestion] = useState(1);
  const [selectedCategory, setSelectedCategory] = useState<number | null>(null);
  const [responses, setResponses] = useState<AssessmentResponse[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Calculate total questions based on selected category
  const getTotalQuestions = () => {
    if (!selectedCategory) {
      return 1; // Just the category selection question
    }
    if (selectedCategory === 1) return 4; // Gender Sensitivity STAR has 4 questions
    if (selectedCategory === 2) return 12; // MSPSS has 12 questions
    return 4; // Default
  };
  
  const totalQuestions = getTotalQuestions();

  // Category selection question
  const categorySelectionQuestion = {
    id: '0',
    question: 'Which Socio-Relational assessment would you like to take?',
    type: 'selection' as const,
    options: [
      'Gender Sensitivity STAR Assessment',
      'Multidimensional Scale of Perceived Social Support'
    ]
  };

  // Gender Sensitivity STAR Assessment - Socio-Relational Domain (4 items)
  const categoryQuestions: { [key: number]: Array<{ id: string; question: string; category: number; type: string }> } = {
    1: [
      { id: '1', question: 'I feel accepted and respected by my family regardless of my gender or orientation.', category: 1, type: 'likert' },
      { id: '2', question: 'I feel safe and included in my workplace/school/community as my true self.', category: 1, type: 'likert' },
      { id: '3', question: 'I have supportive friends or mentors who affirm my gender identity.', category: 1, type: 'likert' },
      { id: '4', question: 'I have experienced discrimination or exclusion based on my gender.', category: 1, type: 'likert' }
    ],
    2: [
      // Significant Other subscale (items 1-4)
      { id: '1', question: 'There is a special person who is around when I am in need.', category: 2, type: 'likert' },
      { id: '2', question: 'There is a special person with whom I can share my joys and sorrows.', category: 2, type: 'likert' },
      { id: '3', question: 'I have a special person who is a real source of comfort to me.', category: 2, type: 'likert' },
      { id: '4', question: 'There is a special person in my life who cares about my feelings.', category: 2, type: 'likert' },
      // Family subscale (items 5-8)
      { id: '5', question: 'My family really tries to help me.', category: 2, type: 'likert' },
      { id: '6', question: 'I get the emotional help and support I need from my family.', category: 2, type: 'likert' },
      { id: '7', question: 'I can talk about my problems with my family.', category: 2, type: 'likert' },
      { id: '8', question: 'My family is willing to help me make decisions.', category: 2, type: 'likert' },
      // Friends subscale (items 9-12)
      { id: '9', question: 'My friends really try to help me.', category: 2, type: 'likert' },
      { id: '10', question: 'I can count on my friends when things go wrong.', category: 2, type: 'likert' },
      { id: '11', question: 'I can talk about my problems with my friends.', category: 2, type: 'likert' },
      { id: '12', question: 'I have friends with whom I can share my joys and sorrows.', category: 2, type: 'likert' }
    ]
  };

  // Get current questions based on selected category
  const getCurrentQuestions = () => {
    if (!selectedCategory) {
      return [categorySelectionQuestion];
    }
    // When category is selected, only return category-specific questions
    return categoryQuestions[selectedCategory as keyof typeof categoryQuestions] || [];
  };

  const questions = getCurrentQuestions();

  // Answer options: 1-5 for STAR, 1-7 for MSPSS
  // Answer options: 1-5 for STAR, 1-7 for MSPSS
  const answerOptions = selectedCategory === 2
    ? [
        { value: 1, label: 'Very Strongly Disagree' },
        { value: 2, label: 'Strongly Disagree' },
        { value: 3, label: 'Mildly Disagree' },
        { value: 4, label: 'Neutral' },
        { value: 5, label: 'Mildly Agree' },
        { value: 6, label: 'Strongly Agree' },
        { value: 7, label: 'Very Strongly Agree' }
      ]
    : [
        { value: 1, label: 'Strongly Disagree' },
        { value: 2, label: 'Disagree' },
        { value: 3, label: 'Neutral' },
        { value: 4, label: 'Agree' },
        { value: 5, label: 'Strongly Agree' }
      ];

  const currentQuestionData = questions[currentQuestion - 1];
  const currentResponse = currentQuestionData ? responses.find(r => r.questionId === currentQuestionData.id) : null;
  const progress = (currentQuestion / totalQuestions) * 100;

  // Type guard to check if question has options (selection type)
  const isSelectionQuestion = (question: any): question is { id: string; question: string; type: 'selection'; options: string[] } => {
    return question.type === 'selection' && 'options' in question;
  };

  // Type guard to check if question has category (likert type)
  const isLikertQuestion = (question: any): question is { id: string; question: string; category: number; type: string } => {
    return question.type === 'likert' && 'category' in question;
  };

  const getCategoryName = (categoryNumber: number) => {
    switch (categoryNumber) {
      case 1: return 'Gender Sensitivity STAR Assessment';
      case 2: return 'Multidimensional Scale of Perceived Social Support';
      default: return 'Unknown Category';
    }
  };

  const saveCurrentResponse = (answer: number) => {
    if (!currentQuestionData) return;
    
    const newResponse: AssessmentResponse = {
      questionId: currentQuestionData.id,
      answer,
      timestamp: new Date()
    };

    setResponses(prev => {
      const filtered = prev.filter(r => r.questionId !== currentQuestionData.id);
      return [...filtered, newResponse];
    });

    // If this is the category selection question, set the selected category
    if (currentQuestionData.id === '0') {
      const categoryMap = [1, 2]; // Map selection index to category number
      setSelectedCategory(categoryMap[answer]);
      // Reset to question 1 for the selected category
      setCurrentQuestion(1);
    }
  };

  const handleNext = () => {
    if (currentQuestion < totalQuestions) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      handleSubmit();
    }
  };

  const handleBack = () => {
    if (currentQuestion > 1) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  const calculateScores = (responses: AssessmentResponse[]) => {
    // Filter out the category selection response (id: '0')
    const assessmentResponses = responses.filter(r => r.questionId !== '0');

    if (selectedCategory === 1) {
      // Gender Sensitivity STAR Assessment scoring
      // Item 4 is reverse-scored (discrimination/exclusion)
      // Higher score on item 4 means MORE discrimination, which is BAD
      // So we need to reverse it: (6 - answer)

      const item1 = assessmentResponses.find(r => r.questionId === '1')?.answer || 0;
      const item2 = assessmentResponses.find(r => r.questionId === '2')?.answer || 0;
      const item3 = assessmentResponses.find(r => r.questionId === '3')?.answer || 0;
      const item4Raw = assessmentResponses.find(r => r.questionId === '4')?.answer || 0;
      const item4 = 6 - item4Raw; // Reverse scoring for discrimination item

      const socioRelationalScore = item1 + item2 + item3 + item4;

      return {
        overall: socioRelationalScore,
        socioRelationalScore: socioRelationalScore
      };
    } else if (selectedCategory === 2) {
      // MSPSS scoring - calculate subscale means and overall mean
      const item1 = assessmentResponses.find(r => r.questionId === '1')?.answer || 0;
      const item2 = assessmentResponses.find(r => r.questionId === '2')?.answer || 0;
      const item3 = assessmentResponses.find(r => r.questionId === '3')?.answer || 0;
      const item4 = assessmentResponses.find(r => r.questionId === '4')?.answer || 0;
      const item5 = assessmentResponses.find(r => r.questionId === '5')?.answer || 0;
      const item6 = assessmentResponses.find(r => r.questionId === '6')?.answer || 0;
      const item7 = assessmentResponses.find(r => r.questionId === '7')?.answer || 0;
      const item8 = assessmentResponses.find(r => r.questionId === '8')?.answer || 0;
      const item9 = assessmentResponses.find(r => r.questionId === '9')?.answer || 0;
      const item10 = assessmentResponses.find(r => r.questionId === '10')?.answer || 0;
      const item11 = assessmentResponses.find(r => r.questionId === '11')?.answer || 0;
      const item12 = assessmentResponses.find(r => r.questionId === '12')?.answer || 0;

      // Calculate subscale means
      const significantOtherMean = (item1 + item2 + item3 + item4) / 4;
      const familyMean = (item5 + item6 + item7 + item8) / 4;
      const friendsMean = (item9 + item10 + item11 + item12) / 4;

      // Calculate overall MSPSS mean
      const overallMSPSS = (item1 + item2 + item3 + item4 + item5 + item6 + item7 + item8 + item9 + item10 + item11 + item12) / 12;

      return {
        overall: overallMSPSS,
        socioRelationalScore: overallMSPSS,
        subscales: {
          significantOther: significantOtherMean,
          family: familyMean,
          friends: friendsMean
        }
      };
    }

    // Default fallback
    return {
      overall: 0,
      socioRelationalScore: 0
    };
  };

  const interpretResults = (scores: any) => {
    const { socioRelationalScore, subscales } = scores;

    let level: 'Low' | 'Moderate' | 'High';
    let interpretation: string;
    let recommendations: string[] = [];

    if (selectedCategory === 1) {
      // Gender Sensitivity STAR Assessment interpretation
      // Scoring range: 4-20 (after reverse scoring item 4)
      // High: 15-20 = High acceptance, safety, support, low discrimination
      // Moderate: 10-14 = Some acceptance and support with areas for growth
      // Low: 4-9 = Low acceptance, safety concerns, high discrimination (red flag)

      if (socioRelationalScore >= 15) {
        level = 'High';
        interpretation = `High Socio-Relational Gender Sensitivity (Score: ${socioRelationalScore}/20). You experience strong acceptance and respect from your family regardless of gender or orientation. You feel safe and included in your workplace, school, or community as your true self. You have supportive friends or mentors who affirm your gender identity, and you report minimal discrimination or exclusion based on gender. This high level of social acceptance and support indicates strong internal self-acceptance and resilience, which are protective factors for your overall well-being.`;
        recommendations = [
          'Continue nurturing the supportive relationships that affirm your identity',
          'Consider being a source of support and mentorship for others who may be struggling',
          'Participate in or support initiatives that promote gender sensitivity and inclusion',
          'Share your positive experiences to help create more inclusive environments',
          'Maintain awareness of your support network as a valuable resource during challenges'
        ];
      } else if (socioRelationalScore >= 10) {
        level = 'Moderate';
        interpretation = `Moderate Socio-Relational Gender Sensitivity (Score: ${socioRelationalScore}/20). You experience some level of acceptance and support from your family, workplace/school/community, and friends or mentors regarding your gender identity. However, there may be areas where you feel less accepted or have experienced some discrimination or exclusion. This moderate level suggests you have some protective factors but may also face challenges that could impact your well-being. Building additional support and addressing areas of concern can enhance your resilience.`;
        recommendations = [
          'Identify and strengthen relationships with people who affirm and support your identity',
          'Seek out support groups or communities where you feel safe and accepted',
          'Consider connecting with a counselor or therapist who specializes in gender identity and inclusion',
          'Develop coping strategies for managing situations where you feel less accepted',
          'Explore resources and organizations that advocate for gender sensitivity and inclusion',
          'Set boundaries with people or environments that don\'t respect your identity'
        ];
      } else {
        level = 'Low';
        interpretation = `Low Socio-Relational Gender Sensitivity (Score: ${socioRelationalScore}/20). ⚠️ RED FLAG: Your score indicates significant challenges with acceptance, safety, and support related to your gender identity. You may experience limited acceptance from family, feel unsafe or excluded in your workplace/school/community, lack supportive friends or mentors, or have experienced considerable discrimination or exclusion. This low level of social acceptance and support is a significant risk factor for stress, depression, and social isolation. It is important to seek professional support and connect with affirming communities.`;
        recommendations = [
          '⚠️ PRIORITY: Consider taking the DASS (Depression, Anxiety, Stress Scale) assessment to evaluate your mental health',
          'Seek immediate support from a mental health professional, particularly one experienced with gender identity issues',
          'Connect with LGBTQ+ support groups, hotlines, or community organizations',
          'Build a safety plan and identify safe spaces and supportive individuals',
          'Consider reaching out to trusted individuals who may provide support',
          'Explore online communities and resources for people with similar experiences',
          'Document experiences of discrimination for potential advocacy or support purposes',
          'Prioritize self-care and activities that affirm your identity and well-being',
          'Remember that your identity is valid and you deserve respect and acceptance'
        ];
      }
    } else if (selectedCategory === 2) {
      // MSPSS interpretation
      // Scoring ranges: 1-7 scale (mean scores)
      // Low: < 3.0, Moderate: 3.0-4.9, High: ≥ 5.0

      const overallLevel = socioRelationalScore >= 5.0 ? 'High' : socioRelationalScore >= 3.0 ? 'Moderate' : 'Low';
      const significantOtherLevel = subscales.significantOther >= 5.0 ? 'High' : subscales.significantOther >= 3.0 ? 'Moderate' : 'Low';
      const familyLevel = subscales.family >= 5.0 ? 'High' : subscales.family >= 3.0 ? 'Moderate' : 'Low';
      const friendsLevel = subscales.friends >= 5.0 ? 'High' : subscales.friends >= 3.0 ? 'Moderate' : 'Low';

      level = overallLevel as 'Low' | 'Moderate' | 'High';

      interpretation = `Multidimensional Scale of Perceived Social Support (MSPSS) Results:\n\nOverall Support Level: ${overallLevel} (${socioRelationalScore.toFixed(2)}/7.0)\n\nSubscale Breakdown:\n• Significant Other Support: ${significantOtherLevel} (${subscales.significantOther.toFixed(2)}/7.0)\n• Family Support: ${familyLevel} (${subscales.family.toFixed(2)}/7.0)\n• Friends Support: ${friendsLevel} (${subscales.friends.toFixed(2)}/7.0)\n\nInterpretation: ${socioRelationalScore >= 5.0 ? 'You report high levels of perceived social support across multiple domains. This strong support network is a significant protective factor for your mental health and well-being.' : socioRelationalScore >= 3.0 ? 'You report moderate levels of perceived social support. While you have some supportive relationships, there may be opportunities to strengthen your support network, particularly in areas where support appears lower.' : '⚠️ RED FLAG: You report low levels of perceived social support across multiple domains. Limited social support is a significant risk factor for mental health challenges including depression, anxiety, and stress. Building stronger support networks should be a priority.'}`;

      if (socioRelationalScore >= 5.0) {
        recommendations = [
          'Continue nurturing your strong support network',
          'Consider being a source of support for others who may need it',
          'Share your positive support experiences to help others build their networks',
          'Maintain regular contact with your supportive relationships',
          'Use your support network proactively during challenging times'
        ];
      } else if (socioRelationalScore >= 3.0) {
        recommendations = [
          'Identify areas where support could be strengthened (significant other, family, or friends)',
          'Consider joining support groups or community organizations',
          'Reach out to existing relationships to deepen connections',
          'Explore counseling or therapy to build social skills and support networks',
          'Participate in social activities to meet new supportive people',
          'Consider volunteering or joining clubs to expand your social circle'
        ];
      } else {
        recommendations = [
          '⚠️ PRIORITY: Seek immediate professional support (counselor, therapist, or psychiatrist)',
          '⚠️ PRIORITY: Consider taking the DASS (Depression, Anxiety, Stress Scale) assessment',
          'Connect with crisis hotlines or support services in your area',
          'Join support groups for people experiencing social isolation',
          'Reach out to any existing relationships, even if they feel distant',
          'Consider working with a social worker to build support networks',
          'Explore online communities while working toward in-person connections',
          'Prioritize self-care and professional mental health support'
        ];
      }
    } else {
      // Default fallback
      level = 'Moderate';
      interpretation = 'Assessment results could not be properly interpreted.';
      recommendations = ['Please contact support if you continue to experience issues.'];
    }

    return { level, interpretation, recommendations };
  };

  const handleSubmit = async () => {
    // Filter out the category selection response for validation
    const assessmentResponses = responses.filter(r => r.questionId !== '0');
    if (assessmentResponses.length < totalQuestions) {
      alert('Please answer all questions before submitting.');
      return;
    }

    setIsSubmitting(true);

    try {
      const scores = calculateScores(responses);
      const results = interpretResults(scores);

      const assessmentData: SocioRelationalData = {
        responses,
        scores,
        results
      };

      const response = await fetch(API_CONFIG.ENDPOINTS.SOCIO_RELATIONAL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        credentials: 'include',
        body: JSON.stringify(assessmentData)
      });

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('SESSION_EXPIRED');
        }
        throw new Error('Failed to submit assessment');
      }

      const result = await response.json();
      
      // Clear Insights page cache to force refresh
      const userId = JSON.parse(localStorage.getItem('user') || '{}').id;
      const insightsCacheKey = userId ? `lifepsensor_insights_cache_${userId}` : 'lifepsensor_insights_cache';
      localStorage.removeItem(insightsCacheKey);
      
      // Notify InsightsPage about the update
      assessmentUpdateNotifier.notifyUpdate('socio-relational');
      
      // Navigate to completion page
      navigate('/panel/learn/socio-relational-assessment/completion', {
        state: { assessmentResult: result.data }
      });

    } catch (error) {

      let errorMessage = 'Error submitting assessment. Please try again.';
      if (error instanceof Error && error.message === 'SESSION_EXPIRED') {
        errorMessage = 'Your session has expired. Please log in again to continue.';
      }
      alert(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitting) {
    return (
      <div className="assessment-form-page">
        <div className="assessment-container">
          <div className="assessment-left-panel">
            <div className="assessment-header">
              <h1>Socio-Relational Assessment</h1>
              <p>Processing your responses...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!currentQuestionData) {
    return (
      <div className="assessment-form-page">
        <div className="assessment-container">
          <div className="assessment-left-panel">
            <div className="assessment-header">
              <h1>Socio-Relational Assessment</h1>
              <p>Loading assessment questions...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="assessment-form-page">
      <div className="assessment-container">
        {/* Left Panel - Background Image */}
        <div
          className="assessment-left-panel socio-relational-panel"
        >
          <div className="assessment-header">
            <h1>Socio-Rational Assessment</h1>
            <p className="assessment-tool-info">
              Evaluating social acceptance, safety, and support in relation to gender identity
            </p>
          </div>
        </div>

        {/* Right Panel - Questions */}
        <div className="assessment-right-panel">
          <div className="assessment-progress">
            <div className="progress-bar">
              <div className="progress-fill" style={{ width: `${progress}%` }}></div>
            </div>
            <span className="progress-text">
              Question {currentQuestion} of {totalQuestions}
            </span>
          </div>

          <div className="question-section">
            {isSelectionQuestion(currentQuestionData) ? (
              <>
                <h2 className="question-title">
                  {currentQuestionData.question}
                </h2>
                <p className="question-instruction">
                  Please select the assessment category you would like to take.
                </p>

                <div className="answer-options selection-options">
                  {currentQuestionData.options.map((option: string, index: number) => (
                    <button
                      key={index}
                      className={`option-button selection-button ${currentResponse?.answer === index ? 'selected' : ''}`}
                      onClick={() => {
                        saveCurrentResponse(index);
                      }}
                    >
                      {option}
                    </button>
                  ))}
                </div>
              </>
            ) : isLikertQuestion(currentQuestionData) ? (
              <>
                <div className="category-indicator">
                  <span className="category-badge">
                    {getCategoryName(currentQuestionData.category)}
                  </span>
                </div>
                
                <h2 className="question-title">
                  {currentQuestionData.question}
                </h2>
                <p className="question-instruction">
                  Please rate how much you agree or disagree with this statement.
                </p>

                <div className="answer-options">
                  {answerOptions.map((option) => (
                    <label key={option.value} className="answer-option">
                      <input
                        type="radio"
                        name={`question-${currentQuestion}`}
                        value={option.value}
                        checked={currentResponse?.answer === option.value}
                        onChange={(e) => saveCurrentResponse(parseInt(e.target.value))}
                      />
                      <span className="option-text">{option.label}</span>
                    </label>
                  ))}
                </div>
              </>
            ) : null}
          </div>

          {/* Hide navigation buttons on category selection page */}
          {selectedCategory !== null && (
            <div className="navigation-buttons">
              <button
                className="btn-secondary"
                onClick={() => {
                  if (currentQuestion === 1 && selectedCategory === null) {
                    navigate('/panel/learn/');
                    return;
                  }
                  if (currentQuestion === 1 && selectedCategory !== null) {
                    setSelectedCategory(null);
                    setCurrentQuestion(1);
                    return;
                  }
                  handleBack();
                }}
                disabled={false}
              >
                Back
              </button>

              <button
                className="btn-primary"
                onClick={handleNext}
                disabled={!currentResponse}
              >
                {currentQuestion === totalQuestions ? 'Complete Assessment' : 'Next'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SocioRelationalAssessmentFormPage;



